import { useState, useRef, useCallback } from "react";

// ─── Recherche entreprise par SIREN/SIRET — API publique gouv ───────────────
// Source UNIQUE (anti-duplication) extraite de InstallateursManager.jsx
// (searchBySiret) + réutilisée par la création dossier OMV.
// Découple l'appel API + debounce (réutilisable) du mapping de champs (propre
// à chaque consommateur, via le retour `mapped` / le callback onResult).

const API_URL = "https://recherche-entreprises.api.gouv.fr/search";

/**
 * Validation Luhn (clé de contrôle SIREN 9ch / SIRET 14ch) — filet contre les
 * erreurs de lecture OCR Vision (confusion 9↔3, 8↔6↔0, 1↔7…). Un numéro complet
 * mais à clé invalide = lecture erronée → on alerte au lieu d'interroger un
 * registre qui ne trouvera rien. Marginal : La Poste (SIREN 356000000) ne suit
 * pas Luhn — hors périmètre bénéficiaires B2B CEE.
 */
export function isLuhnValid(num) {
  const s = (num || "").replace(/\s+/g, "");
  if (!/^\d{9}$|^\d{14}$/.test(s)) return false;
  let sum = 0, alt = false;
  for (let i = s.length - 1; i >= 0; i--) {
    let d = +s[i];
    if (alt) { d *= 2; if (d > 9) d -= 9; }
    sum += d; alt = !alt;
  }
  return sum % 10 === 0;
}

/**
 * Normalise la réponse API → champs exploitables.
 * @param {object} ent — entreprise (unité légale) renvoyée par l'API gouv.
 * @param {string} queriedSiret — valeur interrogée. Si c'est un SIRET précis
 *   (14 chiffres), on privilégie CET établissement (matching_etablissements)
 *   plutôt que le siège : un même SIREN a souvent PLUSIEURS établissements
 *   (mairies, CCAS, hôpitaux, groupes) = clients DISTINCTS, adresses ≠. Sans ce
 *   ciblage, on écraserait le SIRET saisi/extrait (ex. …00031) par celui du
 *   siège (…00015) et son adresse. Défaut "" → comportement siège inchangé.
 */

/**
 * Découpe une adresse FR complète en { rue, cp, ville }. SIRENE (et parfois la
 * saisie) renvoient l'adresse ENTIÈRE ("1 RUE X 70100 GRAY") alors que CP et
 * Ville ont leurs propres champs → sans découpage, le champ "Adresse" contient
 * le CP+ville (irritant Solène). On coupe au 1er code postal (5 chiffres) :
 * rue = avant, cp = le code, ville = après. Si cp/ville connus (SIRENE), on les
 * privilégie. Idempotent : adresse sans CP → rue = adresse telle quelle.
 */
export function splitFrenchAddress(fullAddress, knownCp = "", knownVille = "") {
  const addr = (fullAddress || "").trim();
  if (!addr) return { rue: "", cp: (knownCp || "").trim(), ville: (knownVille || "").trim() };
  const m = addr.match(/^(.*?)[\s,]*\b(\d{5})\b\s*(.*)$/);
  if (m) {
    return {
      rue: m[1].replace(/[\s,]+$/, "").trim(),
      cp: (knownCp || m[2]).trim(),
      ville: (knownVille || m[3]).replace(/^[\s,]+/, "").trim(),
    };
  }
  let rue = addr;
  if (knownCp) rue = rue.replace(new RegExp(`\\b${knownCp}\\b`, "g"), " ");
  if (knownVille) rue = rue.replace(new RegExp(knownVille.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig"), " ");
  return {
    rue: rue.replace(/[\s,]{2,}/g, " ").replace(/^[\s,]+|[\s,]+$/g, "").trim(),
    cp: (knownCp || "").trim(),
    ville: (knownVille || "").trim(),
  };
}

export function mapEntreprise(ent, queriedSiret = "") {
  const siege = ent.siege || {};
  const q = (queriedSiret || "").replace(/\s+/g, "");
  const matched = q.length === 14
    ? (ent.matching_etablissements || []).find((e) => e.siret === q)
    : null;
  const etab = matched || siege;
  const dirigeant = ent.dirigeants?.[0];
  const dirigeantName = dirigeant ? `${dirigeant.prenoms || ""} ${dirigeant.nom || ""}`.trim() : "";
  const cp = (etab.code_postal || "").trim();
  // rue = adresse SANS le CP ni la ville (le champ "Adresse" ne doit JAMAIS
  // contenir le bloc complet — cf. splitFrenchAddress). `adresse` (complète) reste
  // exposé pour rétro-compat, mais les consommateurs DOIVENT poser `rue` dans le
  // champ Adresse + `code_postal`/`ville` dans leurs champs dédiés.
  const _rue = splitFrenchAddress(etab.adresse || "", cp, etab.libelle_commune || "").rue;
  const _siegeRue = splitFrenchAddress(siege.adresse || "", (siege.code_postal || "").trim(), siege.libelle_commune || "").rue;
  return {
    raison_sociale: ent.nom_complet || ent.nom_raison_sociale || "",
    siret: etab.siret || "",
    siren: ent.siren || (etab.siret || "").slice(0, 9),
    adresse: etab.adresse || "",           // COMPLÈTE (rétro-compat)
    rue: _rue,                              // RUE SEULE → champ Adresse
    code_postal: cp,
    ville: etab.libelle_commune || "",
    dept: cp.slice(0, 2),
    dirigeant: dirigeantName,
    nature_juridique: ent.nature_juridique || "",
    // true si l'établissement retenu est le siège (pas de SIRET 14ch ciblé,
    // ou SIRET = celui du siège). Permet à l'appelant de distinguer chantier/siège.
    est_siege: matched ? !!matched.est_siege : true,
    // Enseigne / nom commercial de l'établissement (chantier) — SIRENE.
    nom_etablissement: (etab.liste_enseignes && etab.liste_enseignes[0]) || etab.nom_commercial || "",
    // Adresse du SIÈGE (toujours exposée, depuis ent.siege déjà présent dans la
    // réponse — pas de 2e appel). Permet la bascule bénéficiaire/site quand
    // l'établissement interrogé n'est PAS le siège (est_siege=false).
    siege_adresse: siege.adresse || "",
    siege_rue: _siegeRue,                   // rue seule du siège
    siege_cp: (siege.code_postal || "").trim(),
    siege_ville: siege.libelle_commune || "",
    raw: ent,
  };
}

/**
 * Hook de recherche SIREN/SIRET.
 * @returns {{ searching, info, setInfo, search, searchDebounced }}
 *  - search(raw): Promise<mapped|null> — recherche immédiate, met à jour info.
 *  - searchDebounced(raw, onResult): debounce (défaut 500ms) puis onResult(mapped|null).
 *  - info: { found, message, siege_adresse?, data? } | null
 */
export function useSiretSearch({ debounceMs = 500 } = {}) {
  const [searching, setSearching] = useState(false);
  const [info, setInfo] = useState(null);
  const timerRef = useRef(null);

  const search = useCallback(async (raw) => {
    const clean = (raw || "").replace(/\s+/g, "");
    // Valide uniquement pour SIREN (9) ou SIRET (14)
    if (!/^\d{9}$|^\d{14}$/.test(clean)) {
      setInfo(null);
      return null;
    }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}?q=${clean}&per_page=1`);
      if (!res.ok) {
        setInfo({ found: false, message: "Service API gouv indisponible — saisie manuelle autorisée" });
        return null;
      }
      const data = await res.json();
      const ent = (data.results || [])[0];
      if (!ent) {
        setInfo({ found: false, message: `Aucune entreprise trouvée pour ${clean} — saisie manuelle autorisée` });
        return null;
      }
      const mapped = mapEntreprise(ent, clean);
      setInfo({
        found: true,
        message: `✓ ${mapped.raison_sociale || "Entreprise"} — ${mapped.nature_juridique}`,
        siege_adresse: [mapped.adresse, mapped.code_postal, mapped.ville].filter(Boolean).join(", "),
        data: mapped,
      });
      return mapped;
    } catch (e) {
      setInfo({ found: false, message: "Erreur recherche SIRET — saisie manuelle autorisée" });
      return null;
    } finally {
      setSearching(false);
    }
  }, []);

  const searchDebounced = useCallback((raw, onResult) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const mapped = await search(raw);
      if (onResult) onResult(mapped);
    }, debounceMs);
  }, [search, debounceMs]);

  return { searching, info, setInfo, search, searchDebounced };
}
